import React, { useState, useRef, useEffect, memo } from 'react';
import { Copy, Columns3, EyeClosed, X, PackageCheck, Search, XCircle, Signature, User, Zap, Info } from 'lucide-react';

const QUICK_FILTER_PRESETS = (jiraName) => [
  { label: <><EyeClosed size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Unreviewed</>,          filters: { campaignReviewStatus: ['__none__'] } },
  { label: <><Search size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Review in progress</>,   filters: { campaignReviewStatus: ['In Progress'] } },
  { label: <><Signature size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Created by me</>,         filters: jiraName ? { reporter: [jiraName] } : null },
  { label: <><User size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Assigned to me</>,        filters: jiraName ? { assigneeReviewer: [jiraName] } : null },
  { label: <><PackageCheck size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Development done</>,      filters: { resolution: ['Done'] } },
  { label: <><Copy size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Duplicates</>,            filters: { resolution: ['Duplicate'] } },
];

function isPresetActive(preset, filters) {
  const keys = Object.keys(preset.filters);
  return keys.every(k =>
    JSON.stringify([...(filters[k] ?? [])].sort()) === JSON.stringify([...preset.filters[k]].sort())
  ) && Object.keys(filters).every(k =>
    keys.includes(k) || !filters[k]?.length
  );
}

function PresetsPopover({ jiraName, emptyFilters, filters, onFiltersChange, onClose, anchorRect }) {
  const ref = useRef(null);
  const presets = QUICK_FILTER_PRESETS(jiraName).filter(p => p.filters !== null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const style = anchorRect ? {
    position: 'fixed',
    top: anchorRect.bottom + 6,
    right: window.innerWidth - anchorRect.right,
    left: 'auto',
  } : {};

  return (
    <div className="presets-popup" ref={ref} style={style}>
      <div className="column-selector-header">
        <span>Quick filters</span>
        <button className="column-selector-close" onClick={onClose}><X size={14} /></button>
      </div>
      {presets.map(p => {
        const active = isPresetActive(p, filters);
        return (
          <button
            key={p.label}
            className={`preset-popup-btn${active ? ' active' : ''}`}
            onClick={() => { onFiltersChange(active ? emptyFilters : { ...emptyFilters, ...p.filters }); onClose(); }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

const IssuesToolbar = memo(function IssuesToolbar({
  count,
  onSearchChange,
  includeDesc,
  onIncludeDescChange,
  jiraName,
  filters,
  onFiltersChange,
  emptyFilters,
  onToggleColumns,
}) {
  const [showPresets, setShowPresets] = useState(false);
  const QUICK_FILTER_PRESETS_LIST = QUICK_FILTER_PRESETS(jiraName).filter(p => p.filters !== null);
  const hasActivePreset = QUICK_FILTER_PRESETS_LIST.some(p => isPresetActive(p, filters));
  const [localSearch, setLocalSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  function handleSearchChange(value) {
    setLocalSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(value), 600);
  }

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return (
    <div className="issues-toolbar">
      <div className="issues-toolbar-left">
        <div className="issues-search">
          <Search size={14} className="issues-search-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search issues…"
            value={localSearch}
            onChange={e => handleSearchChange(e.target.value)}
            className="issues-search-input"
          />
          {localSearch && (
            <button className="issues-search-clear" onClick={() => { handleSearchChange(''); inputRef.current?.focus(); }}>
              <XCircle size={14} />
            </button>
          )}
        </div>
        <label className="issues-search-desc-toggle">
          <input
            type="checkbox"
            checked={includeDesc}
            onChange={e => onIncludeDescChange(e.target.checked)}
          />
          incl. description
        </label>
      </div>

      <div className="issues-toolbar-right">
        <div className="issues-count-group">
          <span className="issues-count">
            <strong>{count}</strong> issue{count !== 1 ? 's' : ''}
          </span>
          {hasActivePreset || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : !!v) ? (
            <span className="issues-filters-banner">
              <Info size={12} />
              Filters are applied
            </span>
          ) : null}
        </div>

        <div style={{ position: 'relative' }}>
          <button
            className={`issues-toolbar-btn${showPresets || hasActivePreset ? ' active' : ''}`}
            title="Quick filters"
            onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); setShowPresets(v => v ? false : rect); }}
          >
            <Zap size={15} style={{ flexShrink: 0 }} />
          </button>
          {showPresets && (
            <PresetsPopover
              jiraName={jiraName}
              filters={filters}
              emptyFilters={emptyFilters}
              onFiltersChange={onFiltersChange}
              onClose={() => setShowPresets(false)}
              anchorRect={showPresets}
            />
          )}
        </div>

        <button className="issues-toolbar-btn" title="Toggle columns" onClick={onToggleColumns}>
          <Columns3 size={15} style={{ flexShrink: 0 }} />
        </button>
      </div>
    </div>
  );
});

export default IssuesToolbar;
